type HardReloadReason = "chunk-load" | "boot-timeout" | "manual";

const RELOAD_STATE_KEY = "__app_hard_reload_state";
// Backwards compatibility with earlier builds
const LEGACY_RELOAD_FLAG_KEY = "__app_hard_reload_once";

type ReloadState = {
  count: number;
  lastReason: HardReloadReason;
  lastAt: number;
};

const getSessionState = (): ReloadState | null => {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_STATE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ReloadState>;
      if (typeof parsed.count === "number" && typeof parsed.lastAt === "number" && parsed.lastReason) {
        return parsed as ReloadState;
      }
    }

    // Legacy flag: treat as a single previous reload (prevents loops)
    const legacy = window.sessionStorage.getItem(LEGACY_RELOAD_FLAG_KEY);
    if (legacy) {
      return {
        count: 1,
        lastReason: legacy as HardReloadReason,
        lastAt: Date.now(),
      };
    }
  } catch {
    // ignore
  }
  return null;
};

const setSessionState = (next: ReloadState): void => {
  try {
    window.sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
};

const isChunkError = (err: unknown): boolean => {
  const message =
    typeof err === "string"
      ? err
      : err && typeof err === "object" && "message" in err
        ? String((err as any).message)
        : "";

  const m = message.toLowerCase();

  // Common Vite/React lazy-load failure patterns
  return (
    m.includes("chunkloaderror") ||
    m.includes("loading chunk") ||
    m.includes("failed to fetch dynamically imported module") ||
    m.includes("importing a module script failed") ||
    m.includes("unexpected token <")
  );
};

export async function hardReloadApp(reason: HardReloadReason = "manual"): Promise<void> {
  if (typeof window === "undefined") return;

  // Prevent infinite reload loops (but always allow explicit manual reloads).
  const prev = getSessionState();
  const next: ReloadState = {
    count: (prev?.count ?? 0) + 1,
    lastReason: reason,
    lastAt: Date.now(),
  };

  // If we're auto-recovering repeatedly in the same tab, stop.
  if (reason !== "manual" && next.count > 2) return;
  setSessionState(next);

  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // ignore
  }

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore
  }

  // Clear app-specific localStorage keys (incognito working often indicates stale localStorage state).
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (k.startsWith("__app_") || k.startsWith("tanstack")) toDelete.push(k);
    }
    toDelete.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    // ignore
  }

  // Best-effort IndexedDB cleanup (some browsers may block databases() API).
  try {
    const anyIDB = indexedDB as any;
    if (anyIDB?.databases) {
      const dbs = (await anyIDB.databases()) as Array<{ name?: string }>; // Chromium
      await Promise.all(
        dbs
          .map((d) => d?.name)
          .filter(Boolean)
          .map(
            (name) =>
              new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(String(name));
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              })
          )
      );
    }
  } catch {
    // ignore
  }

  // Force a real navigation (avoids some BFCache edge cases)
  const url = new URL(window.location.href);
  url.searchParams.set("__reload", Date.now().toString());
  window.location.replace(url.toString());
}

export function installRuntimeRecovery(): void {
  if (typeof window === "undefined") return;

  // Expose a manual escape hatch (used by the loader UI)
  (window as any).__hardReloadApp = hardReloadApp;

  // If we already hard-recovered from a chunk-load error this session, don't auto-loop.
  const state = getSessionState();
  if (state?.lastReason === "chunk-load") return;

  const trigger = () => {
    void hardReloadApp("chunk-load");
  };

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkError((event as PromiseRejectionEvent).reason)) {
      try {
        (event as any).preventDefault?.();
      } catch {
        // ignore
      }
      trigger();
    }
  });

  // Capture both ErrorEvents and resource loading errors.
  window.addEventListener(
    "error",
    (event) => {
      const e: any = event;

      if (isChunkError(e?.error) || isChunkError(e?.message)) {
        trigger();
        return;
      }

      const src = e?.target?.src as string | undefined;
      if (src && src.includes("/assets/") && (src.endsWith(".js") || src.endsWith(".css"))) {
        trigger();
      }
    },
    true
  );
}
